export default {
  index: {
    title: '潮汐と潮流のガイド',
    description:
      '潮汐と潮流のしくみを学びましょう：潮汐係数、上げ潮流（フラッド）と下げ潮流（エブ）、潮汐基準面、そして潮汐予測がどのように計算されるか。',
    subtitle: '潮汐と潮流の予測を支える科学を、短くわかりやすく解説します。',
  },
  back: 'すべてのガイドに戻る',
  guides: {
    'tidal-coefficients': {
      title: '潮汐係数とは',
      description:
        '潮汐係数の意味：20から120の尺度、大潮・小潮と月の関係、そして係数が高いほど潮位差が大きくなる理由。',
      blurb: '潮の大きさを要約する20から120の尺度と、それを動かす月のはたらき。',
      body: [
        {
          p: '潮汐係数とは、ある日の潮がどれくらい大きくなるかを一つの数値で表したものです。フランスや、フランスの水路測量の伝統を受け継ぐ国々で広く用いられています。この係数は無次元で、通常は約20から120の範囲をとります。数値が大きいほど、満潮と干潮の差（潮位差）が大きくなり、それに伴う潮流も強くなります。',
        },
        { h2: '20から120の尺度' },
        {
          p: 'この係数は、その日の潮位差を長期的な基準と比較したものです。尺度上のいくつかの値には慣例的な意味があります：',
        },
        {
          list: [
            { term: '20', text: '最も小さい潮で、天文学的な潮位差の最小値付近。' },
            { term: '45', text: '平均的な小潮で、潮位差が小さいとき。' },
            { term: '70', text: '平均潮で、小潮と大潮のおおよそ中間。' },
            { term: '95', text: '平均的な大潮で、潮位差が大きいとき。' },
            { term: '120', text: '最も大きい潮で、天文学的な潮位差の最大値付近。' },
          ],
        },
        { h2: '係数が変わる理由：月' },
        {
          p: '係数はおよそ2週間の大潮・小潮の周期で増減します。係数が高い大潮は、新月と満月のそれぞれ1〜2日後に起こり、このとき太陽と月が一直線に並び、両者の起潮力が足し合わされます。係数が低い小潮は、上弦と下弦の月の頃に起こり、このとき太陽と月が直角の位置にあって、互いの力が部分的に打ち消し合います。',
        },
        {
          p: '月の距離も関係します。大潮が近地点（月が地球に最も近づく点）と重なると、係数は最も高い値に達します。最も大きい潮は、春分と秋分の頃に集中する傾向があります。',
        },
        { h2: '係数からはわからないこと' },
        {
          p: '係数は天文学的な潮位差のみを表します。天候の影響は含まれません。風や気圧は、係数とは関係なく実際の水位を上げ下げします。また、係数はその日の潮全体の性質であって、特定の場所での高さ（メートルやフィート）ではありません。',
        },
      ],
    },
    'tidal-currents': {
      title: '上げ潮流・下げ潮流・憩流：潮流のしくみ',
      description:
        '潮流のしくみ：上げ潮流と下げ潮流の向き、流れが変わるときの憩流、ノットで表す流速と流向（セット）、そして憩流が必ずしも満潮・干潮と一致しない理由。',
      blurb: '上げ潮流、下げ潮流、そして憩流。最も強い流れが必ずしも満潮時ではない理由。',
      body: [
        {
          p: '潮流とは、潮の上下運動（垂直方向の満ち引き）に伴って起こる水の水平方向の動きです。潮位が水の深さを教えてくれるのに対し、潮流は水がどちらの向きにどれくらいの速さで流れているかを教えてくれます。潮流は、水路や入り江、河口で最も重要になり、そこでは数ノットに達することもあります。',
        },
        { h2: '上げ潮流・下げ潮流・憩流' },
        {
          list: [
            {
              term: '上げ潮流',
              text: '岸に向かって、あるいは河口をさかのぼって流れる水。一般に潮位が上昇しているとき。',
            },
            {
              term: '下げ潮流',
              text: '沖に向かって、あるいは河口を下って流れる水。一般に潮位が下降しているとき。',
            },
            {
              term: '憩流',
              text: '上げ潮流と下げ潮流の間で流れが向きを変える際に、流速がほぼゼロになる短い休止。',
            },
          ],
        },
        {
          p: '流速はノット（1時間あたりの海里）で測ります。その向きは流向（セット）と呼ばれ、水が流れていく方向を方位で表します。',
        },
        { h2: '憩流は必ずしも満潮時ではない' },
        {
          p: 'よくある思い込みは、満潮と干潮で流れが止まるというものです。それが当てはまるのは一部の場所だけです。タイミングは、その土地で潮の波がどのように振る舞うかによって決まります。長く開けた水路のように潮が進行波として振る舞う場所では、最も強い上げ潮流と下げ潮流は満潮・干潮の頃に起こり、憩流はその中間付近に来ます。閉じた湾の奥のように定在波として振る舞う場所では逆になり、流れが最も速いのは中間潮位付近で、憩流は満潮・干潮の頃に起こります。頼りになるのは、その地域の潮流表です。',
        },
        { h2: '反転流と回転流' },
        {
          p: '狭い水路では、流れはたいてい反転流です。上げ潮では一方向に流れ、やがて向きを変えて下げ潮では逆向きに流れます。開けた沿岸の海域では、流れはむしろ回転流であることが多く、単一の線に沿って反転するのではなく、一潮汐周期を通じて方位を連続的に変えていきます。',
        },
      ],
    },
    'tidal-datums': {
      title: '潮汐基準面と潮位',
      description:
        '潮位が何を基準に測られるか：海図基準面、最低天文潮位（LAT）、平均低低潮面（MLLW）、そして国によって基準面が異なる理由。',
      blurb: '海図基準面、LAT、MLLW、そして潮位の数値が実際に意味するもの。',
      body: [
        {
          p: 'すべての潮位は、基準面と呼ばれる基準レベルからの高さとして測られます。潮汐表に水位が1.8メートルに達すると書かれていれば、それはその土地の潮汐基準面から1.8メートルという意味であり、海底からでも、日常的な意味での海面からでもありません。基準面を知ることが、高さに意味を与えます。',
        },
        { h2: '潮汐基準面とは' },
        {
          p: '潮汐基準面とは、潮のある特定の段階によって定義される基準の高さで、長期間にわたって平均されたものです。慣例的には約19年で、これは月の軌道にある18.6年周期をならすためです。実際の観測値の平均であるため、基準面はその場所と、それを算出した機関に固有のものになります。',
        },
        { h2: '海図基準面と一般的な基準' },
        {
          p: '海図では低潮の基準面を用いるため、実際の水深はほぼ常に海図に記された水深以上になります。この海図基準面が、海図水深と潮位の両方を測る際のゼロ点です。国によって採用する基準面は異なります：',
        },
        {
          list: [
            {
              term: 'LAT（最低天文潮位）',
              text: '平均的な天候のもとで予測される最も低い水位。イギリスをはじめ多くの国で用いられています。',
            },
            {
              term: 'MLLW（平均低低潮面）',
              text: '毎日2回ある干潮のうち低いほうを平均したもの。アメリカ合衆国における標準的な海図基準面です。',
            },
            {
              term: 'MSL（平均海面）',
              text: '長期にわたる平均水位。海図よりも、主に陸地の標高の基準として用いられます。',
            },
          ],
        },
        {
          p: '平均高潮面（MHW）や平均高高潮面（MHHW）といった高潮の基準面は、逆の目的に用いられます。たとえば、海岸線を定めたり、橋の桁下高（クリアランス）を示したりするためです。',
        },
        { h2: '高さを直接比較できない理由' },
        {
          p: '基準面が異なるため、ある観測所での高さを別の観測所での高さと直接比較することは、両者が同じ基準を用いていない限りできません。LATから測った0.5メートルの干潮は、MLLWから測った0.5メートルの干潮と同じ水位ではありません。同じ物理的な潮が、国によって異なる数値で表される理由もここにあります。',
        },
      ],
    },
    'how-tide-predictions-work': {
      title: '潮汐予測はどのように計算されるか',
      description:
        '潮汐がどのように予測されるか：月と太陽による重力の作用、M2やS2などの調和分潮、そして観測される水位が天文潮とずれる理由。',
      blurb: '調和分解、潮を形づくる分潮、そして天候によって現実がずれる理由。',
      body: [
        {
          p: '潮汐は何年も先まで予測できます。なぜなら、潮汐は月と太陽の運動によって引き起こされ、それらの運動はきわめて正確にわかっているからです。ある場所の潮汐は、その重力の作用に対する海の応答であり、局所的な海岸線と水深によって形づくられます。天文学を潮汐表に変える手法は、調和分解と呼ばれます。',
        },
        { h2: '分潮の総和としての潮汐' },
        {
          p: '調和分解では、潮汐を多数の単純な波の総和として扱い、その一つひとつを分潮と呼びます。各分潮の周波数は天文学によって定まり：地球・月・太陽が互いにどう動くかによって決まり：一方、その振幅と位相は、その場所での実際の水位観測のある期間に分潮を当てはめることで求められます。主な分潮には次のものがあります：',
        },
        {
          list: [
            { term: 'M2', text: '主太陰半日周潮。周期12.42時間で、通常は最も大きい。' },
            { term: 'S2', text: '主太陽半日周潮。周期12.00時間。' },
            { term: 'N2', text: '月の楕円軌道に由来する太陰分潮。' },
            {
              term: 'K1 and O1',
              text: '主要な日周潮。多くの場所で、1日2回の満潮の一方を他方より大きくします。',
            },
          ],
        },
        {
          p: 'おなじみの大潮・小潮の周期は、ここから自然に現れます。M2とS2はわずかに周期が異なるため、次第に位相がずれたり合ったりし、14.8日ごとに強め合って大潮を、部分的に打ち消し合って小潮をつくります。',
        },
        { h2: '先の予測' },
        {
          p: 'ある場所について各分潮の振幅と位相がわかれば、潮汐の予測は、任意の未来の時刻でその総和を計算するだけのことになります。天文学的な周波数は決して変わらないため、計算は決定論的で、何十年も先まで実行できます。',
        },
        { h2: '実際の潮がずれる理由' },
        {
          p: '予測はあくまで天文潮です。天候は含まれません。強風、低気圧や高気圧、大雨、河川の流入は、実際の水位を上げ下げすることがあります。高潮（ストームサージ）は予測より1メートル以上水位を高めることがあり、一方、高気圧と沖に向かう風は水位を予測よりかなり低く抑えることがあります。したがって予測は、潮の時刻や大きさを知るうえで非常に優れた目安ですが、その日に観測される水位は異なることがあります。',
        },
      ],
    },
  },
}
